import { useEffect, useMemo, useState } from 'react';

import { getInitializeFormAttributes, isSaveable, reduceAttributeChange } from './annotationCore';
import { useAppDispatch, useAppSelector, store } from '../../../lib/store';
import { getDefaultVisibility, MODIFIER_VISIBILITY_RULES } from '../config/attributeRules';
import { ATTR_EXPERIENCED_NAME, ATTR_PRESENT_NAME } from '../constants';
import type { Annotation, Attribute, Span } from '../types/responseTypes';
import type { FormAttributeValue } from '../types/types';

interface AnnotationFormSelectors {
  selectAddEvidenceModeForConceptId: (state: any) => string | null;
  selectPendingSpansForAnnotation: (id: string) => (state: any) => Span[];
}

interface AnnotationFormActions {
  clearPendingSpansForAnnotation: (id: string) => any;
  clearHighlightedTextSpans: () => any;
  clearAddEvidenceModeForConceptId: () => any;
  setAddEvidenceModeForConceptId: (id: string) => any;
  setHighlightedTextSpans: (spans: any[]) => any;
}

interface UseAnnotationFormCoreProps {
  annotation: Annotation | null;
  conceptId: string;
  editingAnnotationId?: string;
  attributes: Attribute[] | undefined;
  selectors: AnnotationFormSelectors;
  actions: AnnotationFormActions;
  evidenceIdsToDelete?: string[];
}

export interface UseAnnotationFormCoreResult {
  formAttributes: FormAttributeValue[];
  setFormAttributes: React.Dispatch<React.SetStateAction<FormAttributeValue[]>>;
  getAttributeRule: (name: string) => Attribute | undefined;
  isAttributeVisible: (name: string) => boolean;
  handleChangeAttribute: (name: string, updates: Partial<FormAttributeValue>) => void;
  isPresent: boolean;
  isPresentNull: boolean;
  getTemporaryAnnotationId: () => string;
  currentAnnotationId: string;
  pendingSpans: Span[];
  evidenceRequired: boolean;
  addEvidenceModeForConceptId: string | null;
  startAnnotationMode: (annotation: Annotation | null) => void;
  allRequiredAttributesFilled: boolean;
  enableSaveButton: boolean;
}

export const useAnnotationFormCore = ({
  annotation,
  conceptId,
  editingAnnotationId,
  attributes,
  selectors,
  actions,
  evidenceIdsToDelete = [],
}: UseAnnotationFormCoreProps): UseAnnotationFormCoreResult => {
  const dispatch = useAppDispatch();

  const getAttributeRule = (name: string) => attributes?.find((m) => m.attributeName === name);

  const [formAttributes, setFormAttributes] = useState<FormAttributeValue[]>(() =>
    getInitializeFormAttributes(attributes, annotation)
  );

  // Reinitialize when annotation or attributes change
  useEffect(() => {
    if (attributes) {
      setFormAttributes(getInitializeFormAttributes(attributes, annotation));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotation?.annotationId, attributes]);

  const presentValue = formAttributes.find((m) => m.name === ATTR_PRESENT_NAME)?.value;
  const experiencedValue = formAttributes.find((m) => m.name === ATTR_EXPERIENCED_NAME)?.value;

  const getAttributeValue = useMemo(() => {
    return (name: string) => formAttributes.find((m) => m.name === name)?.value;
  }, [formAttributes]);

  const isAttributeVisible = useMemo(() => {
    return (name: string) => {
      const explicitRule = MODIFIER_VISIBILITY_RULES.find((rule) => rule.attributeName === name);
      if (explicitRule) return explicitRule.isVisible(getAttributeValue);
      return getDefaultVisibility(name, getAttributeValue);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentValue, experiencedValue, getAttributeValue]);

  const handleChangeAttribute = (name: string, updates: Partial<FormAttributeValue>) => {
    if (!attributes) return;
    // Use functional update so multiple calls in the same render batch each see the latest prev
    setFormAttributes((prev) => {
      const { next } = reduceAttributeChange({ prev, attributes, name, updates });
      return next;
    });
    // clearEvidence is determined solely by whether present is being set to false
    const newValue =
      updates.value !== undefined
        ? updates.value
        : formAttributes.find((m) => m.name === name)?.value;
    if (name === ATTR_PRESENT_NAME && newValue === false) {
      dispatch(actions.clearPendingSpansForAnnotation(currentAnnotationId));
      dispatch(actions.clearHighlightedTextSpans());
      dispatch(actions.clearAddEvidenceModeForConceptId());
    }
  };

  const isPresent = formAttributes.find((m) => m.name === ATTR_PRESENT_NAME)?.value === true;
  const isPresentNull = (() => {
    const v = formAttributes.find((m) => m.name === ATTR_PRESENT_NAME)?.value;
    return v === null || v === undefined;
  })();

  const getTemporaryAnnotationId = () => editingAnnotationId || `new-${conceptId}`;
  const currentAnnotationId = annotation?.annotationId || getTemporaryAnnotationId();

  const pendingSpansSelector = useMemo(
    () => selectors.selectPendingSpansForAnnotation(currentAnnotationId),
    [currentAnnotationId]
  );
  const pendingSpans = useAppSelector(pendingSpansSelector);

  const effectiveSavedSpansCount = (annotation?.spans || []).filter(
    (s) => !evidenceIdsToDelete.includes(s.evidenceId!)
  ).length;
  const evidenceRequired = isPresent && effectiveSavedSpansCount + pendingSpans.length === 0;

  const addEvidenceModeForConceptId = useAppSelector(selectors.selectAddEvidenceModeForConceptId);

  const startAnnotationMode = (annotation: Annotation | null) => {
    dispatch(actions.clearHighlightedTextSpans());
    const annotationId = annotation?.annotationId || getTemporaryAnnotationId();
    dispatch(actions.setAddEvidenceModeForConceptId(`${conceptId}#${annotationId}`));

    const savedSpans = (annotation?.spans || [])
      .filter((span: Span) => !evidenceIdsToDelete.includes(span.evidenceId!))
      .map((span: Span, index: number) => ({
        number: index + 1,
        offsetStart: span.offsetStart,
        offsetEnd: span.offsetEnd,
        noteId: span.noteId,
        annotationId: annotation!.annotationId,
        text: span.text,
      }));

    const pendingSpansForHighlight = selectors.selectPendingSpansForAnnotation(annotationId)(
      store.getState()
    );
    const pendingHighlightedSpans = pendingSpansForHighlight.map((span, index) => ({
      number: savedSpans.length + index + 1,
      offsetStart: span.offsetStart,
      offsetEnd: span.offsetEnd,
      noteId: span.noteId,
      annotationId,
      text: span.text,
    }));

    dispatch(actions.setHighlightedTextSpans([...savedSpans, ...pendingHighlightedSpans]));
  };

  const allRequiredAttributesFilled = useMemo(() => {
    if (!attributes) return false;
    return attributes
      .filter((attribute) => attribute.required && isAttributeVisible(attribute.attributeName))
      .every((attribute) => {
        const curr = formAttributes.find((m) => m.name === attribute.attributeName);
        return (
          curr && ((curr.value !== undefined && curr.value !== null) || curr.isUnknown === true)
        );
      });
  }, [attributes, formAttributes, isAttributeVisible]);

  const enableSaveButton = useMemo(() => {
    if (!allRequiredAttributesFilled) return false;
    if (evidenceRequired) return false;
    if (!annotation) return true;
    const attributesChanged = isSaveable(
      annotation.attributes || [],
      formAttributes,
      attributes || []
    );
    return attributesChanged || pendingSpans.length > 0;
  }, [
    allRequiredAttributesFilled,
    evidenceRequired,
    annotation,
    formAttributes,
    pendingSpans.length,
    evidenceIdsToDelete.length,
  ]);

  return {
    formAttributes,
    setFormAttributes,
    getAttributeRule,
    isAttributeVisible,
    handleChangeAttribute,
    isPresent,
    isPresentNull,
    getTemporaryAnnotationId,
    currentAnnotationId,
    pendingSpans,
    evidenceRequired,
    addEvidenceModeForConceptId,
    startAnnotationMode,
    allRequiredAttributesFilled,
    enableSaveButton,
  };
};
